import { eventNames } from 'process'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

const defaultLabels = {
  placeholder: 'Nachnamen suchen',
  noResults: 'Keine Ergebnisse',
  clear: 'Löschen',
}

export const CustomSearch = ({
  data,
  filterType = 'include',
  maxResults = 10,
  small = false,
  labels,
  handleSelect,
  handleFocus,
}: {
  data: any[]
  filterType?: 'include' | 'start'
  maxResults?: number
  small?: boolean
  labels?: {
    placeholder: string
    noResults?: string
    clear?: string
  }
  handleSelect: (item: any) => void
  handleFocus?: () => void
}) => {
  const [showResults, setShowResults] = useState(false)
  const resultRef = useRef<HTMLUListElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [currentFocus, setCurrentFocus] = useState(-1)
  const [results, setResults] = useState<any[]>([])
  const [currentValue, setCurrentValue] = useState('')
  const [preventClearOnBlur, setPreventClearOnBlur] = useState(false)
  const labelsWithDefaults = useMemo(() => {
    return { ...defaultLabels, ...labels }
  }, [labels])

  const handleInput = (e: any) => {
    var searchstring = e.target.value.toLowerCase().trim()

    setResults(
      data
        .filter((d) => {
          if (filterType === 'start') {
            return d.name.toLowerCase().startsWith(searchstring)
          }
          return d.name.toLowerCase().includes(searchstring)
        })
        .sort(
          (a: { name: string }, b: { name: string }) =>
            (b.name.toLowerCase().startsWith(searchstring) ? 1 : 0) -
              (a.name.toLowerCase().startsWith(searchstring) ? 1 : 0) ||
            a.name.length - b.name.length
        )
    )
  }

  const _handleFocus = () => {
    if (handleFocus) handleFocus()
    setCurrentValue('')
    setResults(data)
    setShowResults(true)
  }
  const handleBlur = () => {
    if (!preventClearOnBlur) setShowResults(false)
  }
  if (results.length === 0 && currentValue === '') setResults(data)

  const handleKeyDown = () => {
    if (!resultRef.current) return
    let entries = resultRef.current.children
    if (currentFocus < entries.length - 1) {
      setCurrentFocus(currentFocus + 1)
    } else {
      setCurrentFocus(-1)
    }
  }

  const handleKeyUp = () => {
    if (!resultRef.current) return
    let entries = resultRef.current.children
    if (currentFocus > -1) {
      setCurrentFocus(currentFocus - 1)
    } else {
      setCurrentFocus(entries.length - 1)
    }
  }

  const handleReturn = () => {
    if (currentFocus > -1 && results.length > 0) {
      setCurrentValue(results[currentFocus].name)
      setCurrentFocus(-1)
      inputRef.current?.blur()
      setShowResults(false)
      handleSelect(results[currentFocus])
    } else {
      let n = data.find(
        (d) => d.name.toLowerCase() === currentValue.toLowerCase()
      )

      if (n) {
        setShowResults(false)
        inputRef.current?.blur()
        setCurrentValue(n.name)
        handleSelect(n)
      }
    }
  }

  useEffect(() => {}, [currentFocus])
  return (
    <CustomSearchRoot small={small}>
      <Input
        placeholder={labelsWithDefaults.placeholder}
        small={small}
        ref={inputRef}
        value={currentValue}
        onChange={(e) => {
          setCurrentValue(e.target.value)
          handleInput(e)
        }}
        onFocus={_handleFocus}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          // console.log(e.key)
          if (showResults && e.key === 'ArrowDown') {
            handleKeyDown()
          }
          if (showResults && e.key === 'ArrowUp') {
            handleKeyUp()
          }
          if (showResults && e.key === 'Enter') {
            // e.preventDefault()
            handleReturn()
          }
        }}
      ></Input>
      {showResults && (
        <ResultListContainer
  
        >
          <ResultList ref={resultRef}>
            {results.slice(0, maxResults).map((m, i) => {
              return (
                <ResultListItem
                onMouseEnter={() => setPreventClearOnBlur(true)}
                onMouseOut={() => setPreventClearOnBlur(false)}
                  key={'resultlistitem_' + i}
                  onClick={(e) => {
                    e.bubbles = false
                    handleSelect(m)

                    setCurrentValue(m.name)
                    setCurrentFocus(-1)
                    inputRef.current?.blur()
                    setShowResults(false)

                    //   setCurrentValue(m.name)
                    //   setCurrentFocus(-1)

                    //   setShowResults(false)
                  }}
                  className={
                    'result-list-item ' + (currentFocus === i ? 'focused' : '')
                  }
                  onMouseOver={() => {
                    setCurrentFocus(i)
                  }}
                >
                  {m.name}
                </ResultListItem>
              )
            })}
            {results.length === 0 && currentValue !== '' && (
              <ResultListItem className='result-list-item noresults'>
                {labelsWithDefaults.noResults}
              </ResultListItem>
            )}
          </ResultList>
        </ResultListContainer>
      )}
    </CustomSearchRoot>
  )
}

const CustomSearchRoot = styled.div<{ small: boolean }>`
  margin-bottom: 15px;
  z-index: 100;
  display: block;
  position: relative;
  font-size: 18px;
  width: 100%;
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
  * {
    box-sizing: border-box;
  }

  @media screen and (max-width: 711px) {
    max-width: ${(props) => (props.small ? '100%' : 'calc(100% - 30px);')};
  }
`
const Input = styled.input.attrs({ className: 'custom-search-input' })<{
  small: boolean
}>`
  margin: 0;
  border: 1px solid var(--input-border-color) !important;
  font-size: ${(props) => (props.small ? '16px' : '18px')};

  width: 100%;
  padding: 15px !important;
  border-radius: 4px !important;
  letter-spacing: 0.4px;
  background-color: ${(props) =>
    props.small ? 'var(--map-background)' : 'var(--site-background)'};
  color: var(--text-color);
  &::placeholder {
    color: ${(props) => (props.small ? 'var(--gray-3)' : 'var(--gray-4)')};
  }
`
const ResultList = styled.ul.attrs({ className: 'result-list' })`
  display: inline-block;
  list-style-type: none;
  padding: 0;
  background-color: var(--site-background);
  box-shadow: rgba(0, 0, 0, 0.2) 0px 10px 15px;
  width: 100%;
  position: absolute;
  bottom: -18px;
  transform: translateY(100%);

  /* border: 1px solid var(--text-color); */
`
const ResultListItem = styled.li`
  padding: 5px 10px;
  border-bottom: 1px solid var(--gray-4);

  &.focused {
    background-color: var(--purple-bright-3);
  }
  &:hover {
    cursor: pointer;
    background-color: var(--gray-5);
  }
  &.noresults {
    background-color: var(--yellow-main);
    font-size: 13px;
    line-height: 1.4em;
    font-style: italic;
    &:hover {
      cursor: default;
      background-color: var(--site-background);
    }
  }
`

const ResultListContainer = styled.div.attrs({
  className: 'result-list-container',
})`
  width: 100%;
`
